'use client';

import { produce } from 'immer';
import { atom, selector } from 'recoil';

import {
  checkEveryOptionChecked,
  getSelectedParentChildFilters,
  toggleCheckboxFilters,
} from '@/services/used-cars/used-cars.service';
import { saleFilterState } from '@/states/filters/sale.state';
import {
  ChildFilter,
  ParentFilter,
  SearchFilter,
} from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const searchLocationTypesState = atom<ParentFilter[]>({
  key: 'searchLocationTypes',
  default: [],
});

export const provincesDealershipsState = atom<ParentFilter[]>({
  key: 'provincesAndDealerships',
  default: [],
});

export const provincesDealershipsSelectedFiltersSelector = selector({
  key: 'provincesDealershipsSelectedFilters',
  get: ({ get }) => {
    const provincesDealerships = get(provincesDealershipsState);

    const provincesDealershipsSelectedFilters: SearchFilter[] =
      getSelectedParentChildFilters(provincesDealerships);

    return { provincesDealershipsSelectedFilters };
  },
});

export const saleDateFilterSelector = selector({
  key: 'saleDate',
  get: ({ get }) => {
    const saleDateFilter = get(saleFilterState);

    return saleDateFilter;
  },
});

export const provincesDealershipsAllSelectedSelector = selector({
  key: 'provincesDealershipsAllSelected',
  get: ({ get }) => {
    const provincesDealerships = get(provincesDealershipsState);

    const provincesDealershipsAllSelected: boolean =
      checkEveryOptionChecked(provincesDealerships);

    return { provincesDealershipsAllSelected };
  },
});

export const selectedLocationsNumberInfoSelector = selector({
  key: 'selectedLocationsNumberInfo',
  get: ({ get }) => {
    const selectedLocationsNumberInfo = {
      isOneSelected: false,
      isMultipleSelected: false,
      isAllSelected: false,
      isZeroSelected: false,
    };

    const { provincesDealershipsAllSelected } = get(
      provincesDealershipsAllSelectedSelector,
    );

    const { provincesDealershipsSelectedFilters } = get(
      provincesDealershipsSelectedFiltersSelector,
    );

    if (provincesDealershipsSelectedFilters.length === 1) {
      selectedLocationsNumberInfo.isOneSelected = true;
    } else if (!provincesDealershipsSelectedFilters.length) {
      selectedLocationsNumberInfo.isZeroSelected = true;
    } else if (provincesDealershipsAllSelected) {
      selectedLocationsNumberInfo.isAllSelected = true;
    } else if (provincesDealershipsSelectedFilters.length > 1) {
      selectedLocationsNumberInfo.isMultipleSelected = true;
    }

    return { selectedLocationsNumberInfo };
  },
});

export const searchLocationTypeSearchOptionsSelector = selector({
  key: 'searchLocationTypeSearchOptionsSelector',
  get: ({ get }) => {
    const { searchLocationFilters } = get(searchLocationFiltersSelector);
    const searchLocationTypeSearchOptions: SearchFilter[] =
      searchLocationFilters;

    return { searchLocationTypeSearchOptions };
  },
});

export const searchLocationFiltersSelector = selector({
  key: 'locationSearch',
  get: ({ get }) => {
    const searchLocations = get(searchLocationTypesState);

    const searchLocationFilters: ChildFilter[] = searchLocations.reduce(
      (acc, item) => {
        return [...acc, item, ...(item.children || [])];
      },
      [] as ChildFilter[],
    );

    return { searchLocationFilters };
  },
});

export const toggleProvinceDealershipSelector = selector<
  ParentFilter | ChildFilter | null | undefined
>({
  key: 'toggleProvinceDealership',
  get: () => null,
  set: ({ set, get }, provinceDealership) => {
    const provincesDealerships = get(provincesDealershipsState);

    if (!provinceDealership) return;
    if (guardRecoilDefaultValue(provinceDealership)) return;

    const updateProvincesDealerships = toggleCheckboxFilters(
      provincesDealerships,
      provinceDealership,
    );

    set(provincesDealershipsState, updateProvincesDealerships);
  },
});

export const resetProvincesDealershipsSelector = selector({
  key: 'resetProvincesDealerships',
  get: () => null,
  set: ({ set, get }) => {
    const provincesAndDealerships = get(provincesDealershipsState);
    const updatedProvincesDealerships = produce(
      provincesAndDealerships,
      (draftProvincesDealerships) => {
        draftProvincesDealerships.forEach((province) => {
          province.isChecked = false;

          province.children?.forEach((Dealership) => {
            Dealership.isChecked = false;
          });
        });
      },
    );

    set(provincesDealershipsState, updatedProvincesDealerships);
  },
});
